import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Description,
  Download,
  Delete,
  ArrowBack,
  CheckCircle,
  Visibility,
  CalendarToday,
  Warning,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const InvSupplierDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    type: '',
    file: null,
    expiryDate: '',
  });

  useEffect(() => {
    fetchDocuments();
  }, [id]);

  const fetchDocuments = async () => {
    try {
      const response = await suppliersAPI.getDocuments(id);
      setDocuments(response.data.data.documents);
    } catch (error) {
      enqueueSnackbar('Error fetching documents', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('name', uploadData.name);
      formData.append('type', uploadData.type);
      formData.append('expiryDate', uploadData.expiryDate);
      if (uploadData.file) {
        formData.append('document', uploadData.file);
      }

      await suppliersAPI.uploadDocument(id, formData);
      enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
      setUploadDialog(false);
      setUploadData({ name: '', type: '', file: null, expiryDate: '' });
      fetchDocuments();
    } catch (error) {
      enqueueSnackbar('Error uploading document', { variant: 'error' });
    }
  };

  const handleDownload = async (documentId) => {
    try {
      const response = await suppliersAPI.downloadDocument(id, documentId);
      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      enqueueSnackbar('Error downloading document', { variant: 'error' });
    }
  };

  const handleDelete = async (documentId) => {
    try {
      await suppliersAPI.deleteDocument(id, documentId);
      enqueueSnackbar('Document deleted successfully', { variant: 'success' });
      fetchDocuments();
    } catch (error) {
      enqueueSnackbar('Error deleting document', { variant: 'error' });
    }
  };

  const isExpiringSoon = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return 'none';
    if (isExpired(expiryDate)) return 'expired';
    if (isExpiringSoon(expiryDate)) return 'expiring';
    return 'valid';
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        {/* Back button and header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
          variant="outlined"
        >
          Back to Suppliers
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Supplier Documents
        </Typography>
      </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setUploadDialog(true)}
        >
          Upload Document
        </Button>
      </Box>

      {/* Expiry Alerts */}
      {documents.some(doc => getExpiryStatus(doc.expiryDate) === 'expired') && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Some documents have expired. Please review and update.
        </Alert>
      )}

      {documents.some(doc => getExpiryStatus(doc.expiryDate) === 'expiring') && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Some documents are expiring soon.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Document Summary */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {documents.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Documents
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {documents.filter(doc => getExpiryStatus(doc.expiryDate) === 'expired').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expired
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CalendarToday sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {documents.filter(doc => getExpiryStatus(doc.expiryDate) === 'expiring').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expiring Soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {documents.filter(doc => getExpiryStatus(doc.expiryDate) === 'valid').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valid
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Documents Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                All Documents
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((document) => (
                      <TableRow key={document._id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description color="primary" />
                            <Typography variant="body2" fontWeight="medium">
                              {document.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={document.type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {new Date(document.uploadDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {document.expiryDate ? new Date(document.expiryDate).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {document.expiryDate && (
                            <Chip
                              label={getExpiryStatus(document.expiryDate).toUpperCase()}
                              size="small"
                              color={
                                getExpiryStatus(document.expiryDate) === 'valid' ? 'success' :
                                getExpiryStatus(document.expiryDate) === 'expiring' ? 'warning' : 'error'
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(document._id)}
                            color="primary"
                          >
                            <Download />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(document._id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Name"
                value={uploadData.name}
                onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Document Type"
                value={uploadData.type}
                onChange={(e) => setUploadData(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="license">Business License</MenuItem>
                <MenuItem value="gst_certificate">GST Certificate</MenuItem>
                <MenuItem value="drug_license">Drug License</MenuItem>
                <MenuItem value="quality_certificate">Quality Certificate</MenuItem>
                <MenuItem value="insurance">Insurance</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={uploadData.expiryDate}
                onChange={(e) => setUploadData(prev => ({ ...prev, expiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                Select File
                <input
                  type="file"
                  hidden
                  onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                />
              </Button>
              {uploadData.file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {uploadData.file.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadData.name || !uploadData.type || !uploadData.file}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};